import { RadioCard } from '@almbrand/radiocard';
import classNames from 'classnames';
import { ThemeNames } from 'constantsValues';
import { PageContext } from 'context/PageContext';
import { useFormSubmit } from 'hooks';
import React, { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setCurrentRadioState } from 'store/slices/radioCardSlice';
import { formatNumber } from '../../../../utilities/format';
import styles from './SelectPackageButton.module.scss';

export interface SelectPackageButtonProps {
	header: {
		title: string;
	};
	setSelected: (selected: boolean) => void;
	isSelected?: boolean;
	priceDiscount?: number;
	priceBase?: number;
	hasCalculationError?: boolean;
	noSelectionError?: boolean;
	isLoading?: boolean;
	getIsValid?: (isValid: boolean) => void;
	index: number;
	coverages: CoverGroup[];
	allCalculationsDone: boolean;
	errorMessage?: string;
}

export const SelectPackageButton: React.FC<SelectPackageButtonProps> = ({
	header,
	setSelected,
	isSelected,
	priceDiscount,
	priceBase,
	hasCalculationError,
	noSelectionError,
	isLoading,
	getIsValid,
	index,
	coverages,
	allCalculationsDone,
	errorMessage,
}) => {
	const pageContext = useContext(PageContext);
	const { theme } = pageContext ?? {};
	const themeName = ThemeNames[theme];

	const currentRadioState = useAppSelector((state) => state.currentRadioState);
	const dispatch = useAppDispatch();

	const methods = useForm({ mode: 'onTouched' });
	const { handleSubmit, register } = methods;
	const { onChange, name } = register('SELECTED_PACKAGE');

	const { onSubmit } = useFormSubmit();

	const updatedRadioCardComponentProps = {
		errorMessage: undefined,
	};

	const handleOnChange = async () => {
		if (!isLoading) {
			dispatch(setCurrentRadioState({ index, name }));
			onChange({
				target: {
					name,
					value: {
						id: header.title,
						value: header.title,
						inputLabel: 'Valgte pakke',
						index: 0,
						type: 'PACKAGE',
					},
				},
			});

			onSubmit({
				SELECTED_PACKAGE_MONTHLY_PRICE: {
					id: 'SELECTED_PACKAGE_MONTHLY_PRICE',
					value: formatNumber(priceDiscount ?? priceBase),
					index: 0,
					type: 'PACKAGE',
					inputLabel: 'Pris pr. md. rabat',
				},
			});
			onSubmit({
				SELECTED_PACKAGE_MONTHLY_BASE_PRICE: {
					id: 'SELECTED_PACKAGE_MONTHLY_BASE_PRICE',
					value: formatNumber(priceBase),
					index: 0,
					type: 'PACKAGE',
					inputLabel: 'Pris pr. md. basis',
				},
			});
			coverages.forEach((coverage) => {
				if (coverage.uniqueIdentifier?.uid) {
					onSubmit({
						[coverage.uniqueIdentifier.uid]: {
							id: coverage.uniqueIdentifier.uid,
							value: `${coverage.isActive}`,
							label: coverage.coverName,
							inputLabel: coverage.coverName,
							index: 0,
							type: 'COVERAGE',
						},
					});
				}
			});

			setSelected(true);
			getIsValid(true);

			await handleSubmit(onSubmit)();
		}
	};

	const handleClick = async () => {
		if (!isLoading) {
			await handleOnChange();
		}
	};

	const getTitle = () => {
		if (hasCalculationError) {
			return 'Fejl i beregning';
		}
		if (!allCalculationsDone) {
			return 'Beregner...';
		}
		return isSelected ? 'Valgt' : 'Vælg ' + header.title;
	};

	const isChecked = () => {
		return currentRadioState.some((state) => state.name === name && state.index === index);
	};

	useEffect(() => {
		if (isSelected && priceDiscount) {
			handleOnChange();
		}
	}, [priceDiscount]);

	return (
		<div
			className={classNames(
				styles.SelectPackageButton,
				!allCalculationsDone && styles.SelectPackageButton__waitAllCalculationsDone,
				isChecked() && styles.SelectPackageButton__checked_border
			)}
		>
			<RadioCard
				{...updatedRadioCardComponentProps}
				themeName={themeName}
				title={getTitle()}
				checked={isChecked()}
				onChange={handleClick}
				minWidth='auto'
				hasError={hasCalculationError || noSelectionError}
				errorMessage={errorMessage}
			/>
		</div>
	);
};
